import { type Control, valueFromAttribute } from "./control.js";
import { controlNode } from "./control-nodes.js";

/**
 * The settings panel.
 *
 * Radio groups rather than dropdowns, because every choice here is worth
 * seeing without a click: somebody who does not know that pinyin has a word
 * spacing rule will not go looking in a `<select>` for it, but they will read
 * "Nánjīng Shì, not Nán jīng shì" sitting on the page.
 *
 * The markup is the standard hidden-radio-and-label pattern, styled in
 * `modules/tools.scss`. Bootstrap's `.btn-check` would have done the same job,
 * but the site imports Bootstrap partial by partial and `button-group` is not
 * among them — so using it would mean adding a component to every page on the
 * site for the sake of four.
 */

/**
 * Fill a container with a settings panel and report every change.
 *
 * The controls are passed in rather than reached for, so that the panel knows
 * how to draw a setting without knowing which settings there are. The list
 * this is called with is the pinyin converter's, in `controls.ts`.
 *
 * One listener on the container rather than one per radio: the panel is
 * rebuilt whenever anything else on the page redraws it, and per-input
 * listeners would have to be torn down with it.
 */
export function renderControls<Settings extends object>(
  container: Element,
  controls: readonly Control[],
  settings: Settings,
  idPrefix: string,
  onChange: (changed: Partial<Settings>) => void,
): void {
  container.replaceChildren(
    ...controls.map((control) => controlNode(control, settings, idPrefix)),
  );

  container.addEventListener("change", (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) {
      return;
    }

    const field = input.closest<HTMLElement>("[data-field]")?.dataset["field"];
    const control = controls.find((each) => each.field === field);
    if (control === undefined) {
      return;
    }

    const value = valueFromAttribute(control, input.value);
    if (value !== undefined) {
      // The field is one of the settings' own by construction, which is
      // exactly what a computed key cannot say for itself.
      onChange({ [control.field]: value } as Partial<Settings>);
    }
  });
}
